import { useCallback, useEffect, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useModalId } from "../components/globalModalStack";
import { useQuery } from "./useQuery";

export type SelectableConnector = {
  id: string;
  name: string;
  identifier: string;
  urlPattern: string;
  configuration: Record<string, string> | null;
  note: string | null;
};

// Tracks a just-created connector and fires onSaved once it's actually saved
// (no longer a fresh draft) and its modal has closed — a discarded draft is
// deleted, so abandoning the create fires nothing. Call the returned track()
// with the new connector's id right after opening its connector modal.
export function useConnectorSelectOnSave(
  onSaved: (connector: SelectableConnector) => void
) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const connectorModalId = useModalId("connector");
  const [pending] = useQuery(
    pendingId
      ? queries.loadConnectorDetails({ connectedAppId: pendingId })
      : undefined
  );
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      pendingId &&
      connectorModalId !== pendingId &&
      pending &&
      !pending.fresh &&
      handledRef.current !== pendingId
    ) {
      handledRef.current = pendingId;
      onSaved(pending);
      setPendingId(null);
    }
  }, [connectorModalId, pending, pendingId, onSaved]);

  return useCallback((connectedAppId: string) => {
    handledRef.current = null;
    setPendingId(connectedAppId);
  }, []);
}
